import { useMemo, useCallback } from "react";
import {
  useFloatingIcon,
  useLeftBottomFloatingIcon,
} from "../../../modules/homeDisplay/hooks";
import { ActivityType } from "../../../modules/homeDisplay/types/homeDisplay.types";
import FirstRechargeImg from "@/assets/homePage/FirstRecharge.webp";
import RechargeOfferImg from "@/assets/homePage/RechargeOffer.webp";
import GroupedRechargeTaskFloatingImg from "@/assets/homePage/GroupedRechargeTaskFloating.webp";
import RedPacketRainImg from "@/assets/homePage/RedPacketRain.webp";
import CashBackImg from "@/assets/homePage/cashBack.webp";
import DoubleBetIconImg from "@/assets/bet/doubleBetIcon.webp";
import RechargePromotionImg from "@/assets/homePage/newRecharge.webp";
import GridLotteryIconImg from "@/assets/gridLottery/lottery-icon.webp";
// 同目录直接从具体文件引入，不要走 ./index，避免 useFloatingIcons ↔ 同目录 barrel 的 2-node 环
import { useFirstRechargePopup } from "./useFirstRechargePopup";
import { useCashbackPopup } from "./useCashbackPopup";
import { useRechargeOfferPopup } from "./useRechargeOfferPopup";
import { useRedPacketPopup } from "./useRedPacketPopup";
import { useSevenDayRechargePopup } from "./useSevenDayRechargePopup";
import { useBetActivityPopup } from "./useBetActivityPopup";
import { useRechargePromotionPopup } from "./useRechargePromotionPopup";
import { useGridLotteryPopup } from "./useGridLotteryPopup";
import { useRescueFundPopup } from "./useRescueFundPopup";
import { useRechargeBetPopup } from "./useRechargeBetPopup";
import { useCountdownEndListener } from "./useCountdownEndListener";
import { useFirstRechargeStagedClaimPopup } from "./useFirstRechargeStagedClaimPopup";
import type { FloatingIconConfig } from "./useFloatingIcons.types";
// 直接具体路径，避免走 src/index.ts 桶
import utils from "../../../utils";

/**
 * 所有业务 hooks 的聚合结果类型
 */
type BusinessHooks = {
  firstRecharge: ReturnType<typeof useFirstRechargePopup>;
  firstRechargeStaged: ReturnType<typeof useFirstRechargeStagedClaimPopup>;
  cashback: ReturnType<typeof useCashbackPopup>;
  rechargeOffer: ReturnType<typeof useRechargeOfferPopup>;
  redPacket: ReturnType<typeof useRedPacketPopup>;
  sevenDayRecharge: ReturnType<typeof useSevenDayRechargePopup>;
  betActivity: ReturnType<typeof useBetActivityPopup>;
  rechargePromotion: ReturnType<typeof useRechargePromotionPopup>;
  gridLottery: ReturnType<typeof useGridLotteryPopup>;
  rescueFund: ReturnType<typeof useRescueFundPopup>;
  rechargeBet: ReturnType<typeof useRechargeBetPopup>;
};

/**
 * 纯函数：根据 banners 列表和业务 hooks 结果，构建 FloatingIconConfig 数组
 * 不调用任何 hook，可安全地在 useMemo 中复用
 */
const buildIconsFromBanners = (
  banners: any[],
  hooks: BusinessHooks
): FloatingIconConfig[] => {
  const {
    firstRecharge,
    firstRechargeStaged,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    rechargeBet,
  } = hooks;

  const icons: FloatingIconConfig[] = [];
  let bestGroupedRecharge: { sort: number; config: FloatingIconConfig } | null =
    null;

  for (const banner of banners) {
    const activityType = (banner as any)?.activityType as
      | ActivityType
      | undefined;
    const imageUrl = (banner as any)?.imageUrl || "";
    const sort = (banner as any)?.sort || 0;
    const activityId = (banner as any)?.activityId || "";

    let config: FloatingIconConfig | null = null;
    switch (activityType) {
      case ActivityType.RESCUE_FUND: {
        const rescueFundActivityId =
          (banner as any)?.activityId ?? (banner as any)?.id ?? "";
        if (
          rescueFund.shouldShow &&
          rescueFund.isActivityAvailable?.(rescueFundActivityId)
        ) {
          config = {
            id: `rescue-fund-${banner.id}`,
            activityType,
            sort,
            imageUrl: utils.getImageUrl(imageUrl),
            shouldShow: true,
            onClick: () => {
              rescueFund.trigger(rescueFundActivityId);
            },
            badge: {
              show: rescueFund.getShouldShowBadge(rescueFundActivityId),
              type: "exclamation",
              count: rescueFund.getBadgeCount?.(rescueFundActivityId),
            },
          };
        }
        break;
      }
      case ActivityType.FIRST_DEPOSIT:
      case ActivityType.FIRST_RECHARGE_STAGED: {
        /** 后台可能用 FIRST_DEPOSIT 或 FIRST_RECHARGE_STAGED，需同时匹配。
         * STAGED_MANUAL + showStagedUi 时同图标走分批领取，否则走旧首充营销。 */
        if (firstRechargeStaged.shouldShowFloating) {
          const n = firstRechargeStaged.claimableCount;
          config = {
            id: `first-deposit-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? FirstRechargeImg
                : utils.getImageUrl(imageUrl) || FirstRechargeImg,
            shouldShow: true,
            onClick: firstRechargeStaged.trigger,
            countdown: {
              remainingMs: firstRechargeStaged.remainingMs,
            },
            badge:
              n > 0
                ? {
                    show: true,
                    type: "exclamation" as const,
                    count: n,
                  }
                : undefined,
          };
        } else if (firstRecharge.shouldShow) {
          config = {
            id: `first-deposit-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? FirstRechargeImg
                : utils.getImageUrl(imageUrl) || FirstRechargeImg,
            shouldShow: true,
            onClick: firstRecharge.trigger,
            countdown: {
              remainingMs: firstRecharge.remainingMs,
            },
          };
        }
        break;
      }

      case ActivityType.FIVE_DAILY_RECHARGE:
        if (cashback.shouldShow) {
          config = {
            id: `cashback-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? CashBackImg
                : utils.getImageUrl(imageUrl) || CashBackImg,
            shouldShow: true,
            onClick: cashback.trigger,
            countdown: {
              remainingMs: cashback.remainingMs,
            },
          };
        }
        break;

      case ActivityType.RECHARGE_PROMOTION: {
        const rechargeTaskTrigger =
          rechargeOffer.getTriggerByTaskId(activityId);
        if (rechargeTaskTrigger) {
          const matchedTask = rechargeOffer.rechargeTasks.find(
            t => t.task.taskId === activityId
          );
          if (matchedTask && matchedTask.task.rewardAmount > 0) {
            config = {
              id: `recharge-task-${banner.id}`,
              activityType,
              sort,
              imageUrl:
                utils.getImageUrl(imageUrl) === "/not-found.jpg"
                  ? RechargeOfferImg
                  : utils.getImageUrl(imageUrl) || RechargeOfferImg,
              shouldShow: true,
              onClick: rechargeTaskTrigger,
              countdown: {
                remainingMs: rechargeOffer.remainingMs,
              },
            };
          }
        }
        break;
      }

      case ActivityType.GROUPED_RECHARGE_TASK: {
        // 与首充固定位一致：只展示一个浮标，均指向合并弹窗 "grouped-all"
        const groupedTrigger = rechargeOffer.getTriggerByTaskId(activityId);
        if (groupedTrigger) {
          const combinedEntry = rechargeOffer.rechargeTasks.find(
            t => t.task.taskId === "grouped-all"
          );
          if (combinedEntry && combinedEntry.task.rewardAmount > 0) {
            // 浮标图仅认 displayItem.imageUrl；为空或无效时用包内默认图，不从合并任务 bonusIconUrl 兜底
            const fromDisplay =
              imageUrl && String(imageUrl).trim().length > 0
                ? utils.getImageUrl(imageUrl)
                : null;
            const fromDisplayOk =
              fromDisplay && fromDisplay !== "/not-found.jpg"
                ? fromDisplay
                : null;
            const resolvedUrl = fromDisplayOk || GroupedRechargeTaskFloatingImg;
            const grMs = rechargeOffer.groupedRechargeRemainingMs;
            const candidate: FloatingIconConfig = {
              id: `recharge-task-${banner.id}`,
              activityType,
              sort,
              imageUrl: resolvedUrl,
              shouldShow: true,
              onClick: groupedTrigger,
              badge: {
                show: rechargeOffer.shouldShowGroupedBadge,
                type: "exclamation",
              },
              countdown:
                grMs > 0
                  ? {
                      remainingMs: grMs,
                    }
                  : undefined,
            };
            if (!bestGroupedRecharge || sort > bestGroupedRecharge.sort) {
              bestGroupedRecharge = { sort, config: candidate };
            }
          }
        }
        break;
      }

      case ActivityType.RED_PACKET_RAIN:
        if (redPacket.shouldShow) {
          config = {
            id: `red-packet-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? RedPacketRainImg
                : utils.getImageUrl(imageUrl) || RedPacketRainImg,
            shouldShow: true,
            onClick: redPacket.trigger,
            badge: {
              show: redPacket.isOpen,
              type: "dot",
            },
            countdown: {
              remainingMs: redPacket.remainingMs,
            },
          };
        }
        break;

      case ActivityType.DAILY_RECHARGE:
        if (sevenDayRecharge.shouldShow) {
          config = {
            id: `seven-day-recharge-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? CashBackImg
                : utils.getImageUrl(imageUrl) || CashBackImg,
            shouldShow: true,
            onClick: sevenDayRecharge.trigger,
            badge: {
              show: sevenDayRecharge.shouldShowBadge,
              type: "exclamation",
            },
            countdown: {
              remainingMs: sevenDayRecharge.remainingMs,
            },
          };
        }
        break;

      case ActivityType.BET_TASK:
        if (betActivity.shouldShow) {
          const claimableCount = betActivity.day0ClaimableCount || 0;
          const betTask = betActivity.betTask;
          const displayConfig = betTask?.displayConfig;

          let finalImageUrl = DoubleBetIconImg;
          if (
            betActivity.isDoubleBetActive &&
            displayConfig?.betDoubleFloatingIconImageUrl
          ) {
            finalImageUrl = utils.getImageUrl(
              displayConfig.betDoubleFloatingIconImageUrl
            );
          } else if (
            imageUrl &&
            utils.getImageUrl(imageUrl) !== "/not-found.jpg"
          ) {
            finalImageUrl = utils.getImageUrl(imageUrl);
          }

          config = {
            id: `bet-activity-${banner.id}`,
            activityType,
            sort,
            imageUrl: finalImageUrl,
            shouldShow: true,
            onClick: betActivity.handleBetActivityClick,
            badge: {
              show: betActivity.shouldShowBadge || claimableCount > 0,
              type: "exclamation",
              count: claimableCount > 0 ? claimableCount : undefined,
            },
            countdown: betActivity.showCountdown
              ? {
                  remainingMs: betActivity.remainingMs,
                  isDoubleBet: betActivity.isDoubleBetActive,
                  doubleMultiplier: betActivity.doubleMultiplier,
                }
              : undefined,
          };
        }
        break;

      case ActivityType.RECHARGE_ACTIVITY: {
        const trigger = rechargePromotion.getTriggerByActivityId(activityId);
        if (trigger) {
          const matchedBanner = rechargePromotion.banners.find(
            b => (b as any)?.activityGroupId === activityId
          );
          if (matchedBanner && !rechargePromotion.isAllClaimed(matchedBanner)) {
            const hasClaimable =
              rechargePromotion.hasClaimableUnclaimed(matchedBanner);
            const shouldHideTime =
              rechargePromotion.shouldHideBannerTime(matchedBanner);
            config = {
              id: `recharge-activity-${banner.id}`,
              activityType,
              sort,
              imageUrl:
                utils.getImageUrl(imageUrl) === "/not-found.jpg"
                  ? RechargePromotionImg
                  : utils.getImageUrl(imageUrl) || RechargePromotionImg,
              shouldShow: true,
              onClick: trigger,
              badge: {
                show: hasClaimable,
                type: "exclamation",
              },
              countdown: shouldHideTime
                ? undefined
                : {
                    remainingMs:
                      rechargePromotion.getBannerRemainingMs(matchedBanner),
                  },
            };
          }
        }
        break;
      }

      case ActivityType.RECHARGE_BET: {
        // 充值+投注活动 - 通过 activityId 匹配触发弹窗
        // shouldShow 已在 useRechargeBetPopup 里统一控制过期逻辑，与 RESCUE_FUND 保持一致先检查
        const rechargeBetTrigger =
          rechargeBet.shouldShow &&
          rechargeBet.getTriggerByActivityId(activityId);
        if (rechargeBetTrigger) {
          const remainingMs =
            rechargeBet.getRemainingMsByActivityId(activityId);
          const claimableCount =
            rechargeBet.getClaimableCountByActivityId(activityId);
          const shouldShowBadge =
            rechargeBet.getShouldShowBadgeByActivityId(activityId);
          config = {
            id: `recharge-bet-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? RechargePromotionImg
                : utils.getImageUrl(imageUrl) || RechargePromotionImg,
            shouldShow: true,
            onClick: rechargeBetTrigger,
            badge: {
              show: shouldShowBadge,
              type: "exclamation",
              count: claimableCount > 0 ? claimableCount : undefined,
            },
            countdown:
              Number.isFinite(remainingMs) && remainingMs > 0
                ? { remainingMs }
                : undefined,
          };
        }
        break;
      }

      case ActivityType.NINE_GRID_ACTIVITY:
        if (gridLottery.shouldShow) {
          config = {
            id: `grid-lottery-${banner.id}`,
            activityType,
            sort,
            imageUrl:
              utils.getImageUrl(imageUrl) === "/not-found.jpg"
                ? GridLotteryIconImg
                : utils.getImageUrl(imageUrl) || GridLotteryIconImg,
            shouldShow: true,
            onClick: gridLottery.trigger,
            badge: {
              show: gridLottery.shouldShowBadge,
              type: "exclamation",
              count: gridLottery.claimableCount > 0 ? 1 : undefined,
            },
            countdown:
              gridLottery.remainingMs > 0
                ? { remainingMs: gridLottery.remainingMs }
                : undefined,
          };
        }
        break;

      default:
        break;
    }

    if (config) {
      icons.push(config);
    }
  }

  if (bestGroupedRecharge) {
    icons.push(bestGroupedRecharge.config);
  }

  return icons.sort((a, b) => a.sort - b.sort);
};

/**
 * 所有业务 hooks 只调用一次，同时计算右下角和左下角的图标数据
 * 避免业务 hooks（弹窗注册等）被重复实例化
 */
export const useFloatingIcons = () => {
  const floatingIconBanners = useFloatingIcon();
  const leftBottomBanners = useLeftBottomFloatingIcon();

  const firstRecharge = useFirstRechargePopup();
  const cashback = useCashbackPopup();
  const rechargeOffer = useRechargeOfferPopup();
  const redPacket = useRedPacketPopup();
  const sevenDayRecharge = useSevenDayRechargePopup();
  const betActivity = useBetActivityPopup();
  const rechargePromotion = useRechargePromotionPopup();
  const gridLottery = useGridLotteryPopup();
  const rescueFund = useRescueFundPopup();
  const firstRechargeStaged = useFirstRechargeStagedClaimPopup();
  const rechargeBet = useRechargeBetPopup();

  useCountdownEndListener(firstRecharge.remainingMs);
  useCountdownEndListener(cashback.remainingMs);
  useCountdownEndListener([
    rechargeOffer.remainingMs,
    rechargeOffer.groupedRechargeRemainingMs,
  ]);
  useCountdownEndListener(redPacket.remainingMs);
  useCountdownEndListener(sevenDayRecharge.remainingMs);
  useCountdownEndListener(betActivity.remainingMs);
  useCountdownEndListener(gridLottery.remainingMs);
  useCountdownEndListener(firstRechargeStaged.remainingMs);
  // useCountdownEndListener(rescueFund.remainingMs); // 救援金不显示倒计时

  const rechargePromotionRemainingMsList = useMemo(
    () =>
      rechargePromotion.banners
        .map(banner => rechargePromotion.getBannerRemainingMs(banner))
        .filter(ms => Number.isFinite(ms) && ms > 0),
    [rechargePromotion.banners, rechargePromotion.getBannerRemainingMs]
  );
  useCountdownEndListener(rechargePromotionRemainingMsList);

  const businessHooks: BusinessHooks = {
    firstRecharge,
    firstRechargeStaged,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    rechargeBet,
  };

  // 右下角图标
  const floatingIcons = useMemo(
    () => buildIconsFromBanners(floatingIconBanners, businessHooks),
    [
      floatingIconBanners,
      firstRecharge,
      firstRechargeStaged,
      cashback,
      rechargeOffer,
      rechargeOffer.groupedRechargeRemainingMs,
      redPacket,
      sevenDayRecharge,
      betActivity,
      rechargePromotion,
      gridLottery,
      rescueFund,
      rechargeBet,
    ]
  );

  // 左下角图标（CUSTOM 跳转 + ACTIVITY 活动，共用同一份业务 hooks）
  const leftBottomFloatingIcons = useMemo(() => {
    const customBanners: any[] = [];
    const activityBanners: any[] = [];

    leftBottomBanners.forEach(banner => {
      const activityType = (banner as any)?.activityType;
      const sourceType = (banner as any)?.sourceType;
      if (activityType || sourceType === "ACTIVITY") {
        activityBanners.push(banner);
      } else {
        customBanners.push(banner);
      }
    });

    const customIcons: FloatingIconConfig[] = customBanners
      .map(banner => {
        const imageUrl = utils.getImageUrl((banner as any)?.imageUrl || "");
        if (!imageUrl || imageUrl === "/not-found.jpg") return null;
        const redirectUrl =
          (banner as any)?.redirectUrl || (banner as any)?.internalRoute || "";
        const sort = (banner as any)?.sort || 0;
        const id = (banner as any)?.id || "";
        const leftBottomFloatingEnabled =
          (banner as any)?.leftBottomFloatingEnabled ?? false;
        return {
          id: `left-bottom-custom-${id}`,
          sort,
          imageUrl,
          shouldShow: true,
          redirectUrl,
          leftBottomFloatingEnabled,
          onClick: () => {},
        } as FloatingIconConfig;
      })
      .filter(Boolean) as FloatingIconConfig[];

    const activityIcons = buildIconsFromBanners(activityBanners, businessHooks);

    return [...customIcons, ...activityIcons].sort((a, b) => a.sort - b.sort);
  }, [
    leftBottomBanners,
    firstRecharge,
    firstRechargeStaged,
    cashback,
    rechargeOffer,
    rechargeOffer.groupedRechargeRemainingMs,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    rechargeBet,
  ]);

  const PopupRegistrationComponent = useCallback(() => {
    const RechargePromotionPopups =
      rechargePromotion.PopupRegistrationComponent;
    const RechargeOfferPopups = rechargeOffer.PopupRegistrationComponent;
    const RechargeBetPopups = rechargeBet.PopupRegistrationComponent;
    return (
      <>
        <RechargePromotionPopups />
        <RechargeOfferPopups />
        <RechargeBetPopups />
      </>
    );
  }, [
    rechargePromotion.PopupRegistrationComponent,
    rechargeOffer.PopupRegistrationComponent,
    rechargeBet.PopupRegistrationComponent,
  ]);

  return {
    floatingIcons,
    leftBottomFloatingIcons,
    firstRecharge,
    cashback,
    rechargeOffer,
    redPacket,
    sevenDayRecharge,
    betActivity,
    rechargePromotion,
    gridLottery,
    rescueFund,
    PopupRegistrationComponent,
  };
};

/**
 * 左下角浮窗图标 hook
 * 复用 useFloatingIcons 内已初始化的业务 hooks，不重复实例化
 */
export const useLeftBottomFloatingIcons = () => {
  const { leftBottomFloatingIcons, PopupRegistrationComponent } =
    useFloatingIcons();
  return {
    floatingIcons: leftBottomFloatingIcons,
    PopupRegistrationComponent,
  };
};
